import React, {useState} from "react";
import {Popconfirm, Tooltip, Badge} from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    UploadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    BellOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import DataTablePropTypes from "./ComponentPropTypes";
import {TableContainer, ActionButtons} from "./ComponentStyles";
import UpdateModal from "./UpdateModal/UpdateModal";
import ExcelUploadModal from "./ExcelUploadModal/ExcelUploadModal";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const DataTable = ({
    data,
    setData,
    loading,
    setReloadData,
    currentPage,
    setCurrentPage,
    dataCount,
    selectedItem,
    setSelectedItem,
    showExcelUploadModal,
    setShowExcelUploadModal,
    setRecall
}: DataTablePropTypes) => {
    const {t} = useTranslation(["paymentManagement"]);
    const stores = useStore();
    const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [notifyLoading, setNotifyLoading] = useState(false);
    const [notifyTestLoading, setNotifyTestLoading] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);

    const closeUpdateModal = (reloadData = false) => {
        if (reloadData) setReloadData();
        setShowUpdateModal(false);
        setSelectedItem();
    };
    const closeExcelUploadModal = (reloadData = false) => {
        setRecall(prev => !prev);
        if (reloadData) setReloadData();
        setShowExcelUploadModal(false);
        setSelectedItem();
    };

    const deleteAction = async id => {
        setDeleteLoading(true);
        await stores.paymentPeriodStore.deletePaymentPeriod({_id: id});
        setReloadData();
        setDeleteLoading(false);
    };

    const notifyAction = async id => {
        setNotifyLoading(id);
        await stores.paymentPeriodStore.sentNotificationPaymentPeriod({
            _id: id,
            isTestNotification: false
        });
        setReloadData();
        setNotifyLoading(false);
    };

    const notifyTestAction = async id => {
        setNotifyTestLoading(id);
        await stores.paymentPeriodStore.sentNotificationPaymentPeriod({
            _id: id,
            isTestNotification: true
        });
        setReloadData();
        setNotifyTestLoading(false);
    };

    const renderActionButtons = (id, item) => {
        const canUpdate = true;

        const permisions = {
            upload: canUpdate,
            update: canUpdate,
            delete: canUpdate,
            sendNotification: canUpdate
        };
        return (
            <ActionButtons>
                {permisions.upload && (
                    <Tooltip title={t("actions.upload", {ns: "common"})}>
                        <CustomButton
                            icon={<UploadOutlined />}
                            data-testid="importFromExcelButton"
                            onClick={() => {
                                setSelectedItem(item);
                                setShowExcelUploadModal(true);
                            }}
                        ></CustomButton>
                    </Tooltip>
                )}
                {permisions.sendNotification && (
                    <Tooltip title={t("actions.notify", {ns: "common"})}>
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                await notifyAction(item._id);
                            }}
                            onCancel={() => {}}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <CustomButton
                                loading={notifyLoading === id}
                                data-testid="notifyButton"
                                icon={<BellOutlined />}
                            ></CustomButton>
                        </Popconfirm>
                    </Tooltip>
                )}
                {permisions.sendNotification && (
                    <Tooltip title={t("actions.notifyTest", {ns: "common"})}>
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                await notifyTestAction(item._id);
                            }}
                            onCancel={() => {}}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <Badge dot={true} offset={[-10, 5]} color={"blue"}>
                                <CustomButton
                                    loading={notifyTestLoading === id}
                                    data-testid="notifyTestButton"
                                    icon={<BellOutlined />}
                                ></CustomButton>
                            </Badge>
                        </Popconfirm>
                    </Tooltip>
                )}

                {permisions.update && (
                    <Tooltip title={t("actions.update", {ns: "common"})}>
                        <CustomButton
                            data-testid="updateButton"
                            disabled={deleteConfirmLoading}
                            loading={deleteConfirmLoading === id}
                            onClick={() => {
                                setSelectedItem(item);
                                setShowUpdateModal(true);
                            }}
                            icon={<EditOutlined />}
                        ></CustomButton>
                    </Tooltip>
                )}

                {permisions.delete && (
                    <Tooltip title={t("actions.delete", {ns: "common"})}>
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                await deleteAction(item._id);
                            }}
                            onCancel={() => {}}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <CustomButton
                                loading={deleteLoading && id}
                                data-testid="deleteButton"
                                icon={<DeleteOutlined />}
                            ></CustomButton>
                        </Popconfirm>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    return (
        <>
            <UpdateModal
                visibility={Boolean(selectedItem?._id) && showUpdateModal}
                onCancel={closeUpdateModal}
                id={selectedItem?._id}
                startDate={moment(selectedItem?.startDate)}
                endDate={moment(selectedItem?.endDate)}
                name={selectedItem?.name}
                invoiceDate={moment(selectedItem?.invoiceDate)}
                paymentDate={moment(selectedItem?.paymentDate)}
                periodType={selectedItem?.periodType || "default"}
            />
            <ExcelUploadModal
                visibility={Boolean(selectedItem?._id) && showExcelUploadModal}
                onClose={closeExcelUploadModal}
                id={selectedItem?._id}
                name={selectedItem?.name}
            />
            <TableContainer
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                rowKey="_id"
                dataSource={data}
                data-testid="paymentPeriodTable"
                loading={loading}
                display={data.length > 0 ? "grid" : "flex"}
                dataTitles={[
                    "Dönem (Gün Ay Yıl)",
                    "Kurye Sayısı",
                    "Gönderilen",
                    "İletilen",
                    "Görülen",
                    t("actions.action", {ns: "common"})
                ]}
            >
                <TableContainer.Column
                    dataIndex="name"
                    title={"Dönem (Gün Ay Yıl)"}
                    //title={t("fields.date", {ns: "common"})}
                    render={name => <span data-testid="name">{!name ? "-" : name}</span>}
                />
                <TableContainer.Column
                    dataIndex="couriers"
                    title={"Kurye Sayısı"}
                    //title={t("fields.date", {ns: "common"})}
                    render={couriers => (
                        <span data-testid="couriers">
                            {!couriers && isNaN(couriers) ? "-" : couriers}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="informed"
                    title={"Gönderilen"}
                    //title={t("fields.date", {ns: "common"})}
                    render={informed => (
                        <span data-testid="informed">
                            {!informed && isNaN(informed) ? "-" : informed}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="received"
                    title={"İletilen"}
                    //title={t("fields.date", {ns: "common"})}
                    render={received => (
                        <span data-testid="received">
                            {!received && isNaN(received) ? "-" : received}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="seen"
                    title={"Görülen"}
                    //title={t("fields.date", {ns: "common"})}
                    render={seen => (
                        <span data-testid="seen">{!seen && isNaN(seen) ? "-" : seen}</span>
                    )}
                />

                <TableContainer.Column
                    align="right"
                    title={t("actions.action", {ns: "common"})}
                    dataIndex="_id"
                    render={renderActionButtons}
                />
            </TableContainer>
        </>
    );
};

export default DataTable;
